// NEW_FILE_JUSTIFIED: New component for displaying and managing learned AI reply rules
import { useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { ILearnedRulesListProps, ILearnedRule, TRuleType } from '@/@types/aiReply';
import { RULE_TYPE_LABELS } from '@/@types/aiReply';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: need Id type

const RULE_TYPE_COLORS: Record<TRuleType, string> = {
  always_do: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  never_do: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  prefer: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  avoid: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
};

export function LearnedRulesList({
  rules,
  platform,
  onAddRule,
  onUpdateRule,
  onToggleRule,
  onDeleteRule,
}: ILearnedRulesListProps) {
  // LOCAL_TYPE_JUSTIFIED: inline edit state scoped to this component
  const [editingId, setEditingId] = useState<Id<'aiLearnedRules'> | null>(null);
  const [editText, setEditText] = useState('');

  const handleStartEdit = useCallback((rule: ILearnedRule) => {
    setEditingId(rule._id);
    setEditText(rule.rule);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (editingId === null || editText.trim() === '') return;
    await onUpdateRule(editingId, { rule: editText.trim() });
    setEditingId(null);
    setEditText('');
  }, [editingId, editText, onUpdateRule]);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditText('');
  }, []);

  if (rules.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          No rules yet. Rules are learned automatically when you edit AI drafts, or you can add them
          manually.
        </p>
        <AddRuleForm platform={platform} onAddRule={onAddRule} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {rules.map(rule => (
        <div
          key={rule._id}
          className={cn(
            'flex items-center gap-2 rounded-md border p-2',
            !rule.isActive && 'opacity-50'
          )}
        >
          <Badge
            variant="secondary"
            className={cn('shrink-0 text-xs', RULE_TYPE_COLORS[rule.ruleType])}
          >
            {RULE_TYPE_LABELS[rule.ruleType]}
          </Badge>

          {editingId === rule._id ? (
            <div className="flex flex-1 items-center gap-1">
              <Input
                value={editText}
                onChange={e => setEditText(e.target.value)}
                className="h-7 text-sm"
                onKeyDown={e => {
                  if (e.key === 'Enter') void handleSaveEdit();
                  if (e.key === 'Escape') handleCancelEdit();
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => void handleSaveEdit()}
              >
                <Check aria-hidden="true" className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleCancelEdit}>
                <X aria-hidden="true" className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <span className="flex-1 text-sm">{rule.rule}</span>
          )}

          {rule.source === 'auto' && rule.observedCount > 1 && editingId !== rule._id && (
            <span className="shrink-0 text-xs text-muted-foreground">{rule.observedCount}x</span>
          )}

          {editingId !== rule._id && (
            <div className="flex shrink-0 items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => handleStartEdit(rule)}
              >
                <Pencil aria-hidden="true" className="h-3 w-3" />
              </Button>
              <Switch
                checked={rule.isActive}
                onCheckedChange={checked => void onToggleRule(rule._id, checked)}
                className="scale-75"
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => void onDeleteRule(rule._id)}
              >
                <Trash2 aria-hidden="true" className="h-3 w-3 text-destructive" />
              </Button>
            </div>
          )}
        </div>
      ))}

      <AddRuleForm platform={platform} onAddRule={onAddRule} />
    </div>
  );
}

interface IAddRuleFormProps {
  // LOCAL_TYPE_JUSTIFIED: internal sub-component props
  platform?: ILearnedRulesListProps['platform'];
  onAddRule: ILearnedRulesListProps['onAddRule'];
}

const VALID_RULE_TYPES: TRuleType[] = ['always_do', 'never_do', 'prefer', 'avoid'];

function isRuleType(val: string): val is TRuleType {
  return VALID_RULE_TYPES.includes(val as TRuleType); // AS_JUSTIFIED: type guard implementation
}

function useAddRuleForm(
  platform: IAddRuleFormProps['platform'],
  onAddRule: IAddRuleFormProps['onAddRule']
) {
  const [isAdding, setIsAdding] = useState(false);
  const [newRule, setNewRule] = useState('');
  const [newRuleType, setNewRuleType] = useState<TRuleType>('always_do');

  const handleAdd = useCallback(async () => {
    if (newRule.trim() === '') return;
    await onAddRule(newRule.trim(), newRuleType, platform);
    setNewRule('');
    setNewRuleType('always_do');
    setIsAdding(false);
  }, [newRule, newRuleType, platform, onAddRule]);

  const reset = useCallback(() => setIsAdding(false), []);
  const open = useCallback(() => setIsAdding(true), []);

  return { isAdding, newRule, setNewRule, newRuleType, setNewRuleType, handleAdd, reset, open };
}

function AddRuleForm({ platform, onAddRule }: IAddRuleFormProps) {
  const { isAdding, newRule, setNewRule, newRuleType, setNewRuleType, handleAdd, reset, open } =
    useAddRuleForm(platform, onAddRule);

  if (!isAdding) {
    return (
      <Button variant="outline" size="sm" onClick={open}>
        <Plus aria-hidden="true" className="mr-1 h-3.5 w-3.5" />
        Add Rule
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2 rounded-md border border-dashed p-2">
      <Select
        value={newRuleType}
        onValueChange={val => {
          if (isRuleType(val)) setNewRuleType(val);
        }}
      >
        <SelectTrigger className="h-7 w-24 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="always_do">Always</SelectItem>
          <SelectItem value="never_do">Never</SelectItem>
          <SelectItem value="prefer">Prefer</SelectItem>
          <SelectItem value="avoid">Avoid</SelectItem>
        </SelectContent>
      </Select>
      <Input
        value={newRule}
        onChange={e => setNewRule(e.target.value)}
        placeholder="e.g., Introduce yourself after greeting"
        className="h-7 flex-1 text-sm"
        onKeyDown={e => {
          if (e.key === 'Enter') void handleAdd();
          if (e.key === 'Escape') reset();
        }}
        autoFocus
      />
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => void handleAdd()}>
        <Check aria-hidden="true" className="h-3.5 w-3.5" />
      </Button>
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={reset}>
        <X aria-hidden="true" className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
